import React, { useEffect, useState } from 'react';
import moment from 'moment';

import { CREATE, UPDATE, provider } from '../../../_utils';
import Utils from '../utils';
import '../../operators.css';

const { contractTypes, parttimeTypes } = Utils.options();

const EMPTY = {
    type: '',
    number: '',
    start: '',
    end: '',
    parttime: '',
    is_primary: false,
    assignment: false,
    assignment_date: '',
    early_termination_date: '',
    comment: '',
};

const toYmd = (value) => {
    if (!value) return '';
    const date = moment(value);
    return date.isValid() ? date.format('YYYY-MM-DD') : '';
};

const fromContract = (contract) => {
    if (!contract) return { ...EMPTY };
    const type = contract.type || '';
    const parttime = type === 'Contract' && !contract.parttime ? 'Full' : (contract.parttime || '');
    return {
        type,
        number: contract.number || '',
        start: toYmd(contract.start),
        end: toYmd(contract.end),
        parttime,
        is_primary: !!contract.is_primary,
        assignment: !!contract.assignment,
        assignment_date: toYmd(contract.assignment_date),
        early_termination_date: toYmd(contract.early_termination_date),
        comment: contract.comment || '',
    };
};

const Field = ({ label, span, required, error, children }) => (
    <label className={`pc-emp-fl${span ? ` pc-emp-fl--${span}` : ''}${error ? ' is-invalid' : ''}`}>
        <span>
            {label}
            {required ? <i className="pc-emp-req">*</i> : null}
        </span>
        {children}
        {error ? <em className="pc-emp-ferr">{error}</em> : null}
    </label>
);

const validateContract = (form) => {
    const errors = {};
    if (!form.type) errors.type = 'Укажите тип';
    if (!String(form.number || '').trim()) errors.number = 'Укажите номер';
    if (!form.start) errors.start = 'Укажите дату начала';
    if (!form.end) errors.end = 'Укажите дату окончания';
    if (form.start && form.end && moment(form.end).isBefore(form.start, 'day')) {
        errors.end = 'Не может быть раньше даты начала';
    }
    if (form.type === 'Contract' && !form.parttime) {
        errors.parttime = 'Укажите ставку';
    }
    if (form.type === 'Contract' && form.assignment && !form.assignment_date) {
        errors.assignment_date = 'Укажите дату распределения';
    }
    if (form.early_termination_date && form.start && moment(form.early_termination_date).isBefore(form.start, 'day')) {
        errors.early_termination_date = 'Не раньше даты начала';
    }
    if (form.early_termination_date && form.end && moment(form.early_termination_date).isAfter(form.end, 'day')) {
        errors.early_termination_date = 'Не позже даты окончания';
    }
    return errors;
};

const ContractModal = ({ open, onClose, contract, operatorId, onSave }) => {
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [touched, setTouched] = useState({});
    const [submitted, setSubmitted] = useState(false);
    const isEdit = Boolean(contract && (contract._id || contract.id));
    const isContractor = form.type === 'Contractor';
    const isContract = form.type === 'Contract';
    const errors = validateContract(form);
    const canSave = Object.keys(errors).length === 0;
    const showError = (key) => (submitted || touched[key]) ? errors[key] : '';

    useEffect(() => {
        if (!open) return undefined;
        setForm(fromContract(contract));
        setSaving(false);
        setTouched({});
        setSubmitted(false);
        const onKey = (event) => {
            if (event.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, contract, onClose]);

    if (!open) return null;

    const mark = (key) => setTouched((prev) => ({ ...prev, [key]: true }));

    const set = (key) => (event) => {
        const value = event.target.value;
        setForm((prev) => ({ ...prev, [key]: value }));
    };
    const setCheck = (key) => (event) => {
        const checked = event.target.checked;
        setForm((prev) => ({ ...prev, [key]: checked }));
    };

    const setType = (event) => {
        const type = event.target.value;
        setForm((prev) => ({
            ...prev,
            type,
            parttime: type === 'Contract' ? (prev.parttime || 'Full') : '',
            is_primary: type === 'Contract' ? prev.is_primary : false,
            assignment: type === 'Contract' ? prev.assignment : false,
            assignment_date: type === 'Contract' ? prev.assignment_date : '',
        }));
        mark('type');
    };

    const handleSave = async () => {
        if (saving) return;
        setSubmitted(true);
        setTouched({
            type: true,
            number: true,
            start: true,
            end: true,
            parttime: true,
            assignment_date: true,
            early_termination_date: true,
        });
        if (!canSave) return;
        setSaving(true);
        try {
            const data = {
                operatorId,
                type: form.type,
            };
            if (form.start) data.start = form.start;
            if (form.end) data.end = form.end;
            if (form.number) data.number = form.number;

            if (!isContractor && isContract) {
                if (form.parttime) data.parttime = form.parttime;
                data.assignment = !!form.assignment;
                if (form.assignment_date) data.assignment_date = form.assignment_date;
                if (form.parttime && form.parttime !== 'Full') {
                    data.is_primary = !!form.is_primary;
                }
            }

            if (form.early_termination_date) data.early_termination_date = form.early_termination_date;
            if (form.comment) data.comment = form.comment;

            if (isEdit) {
                if (contract.extended_until && Array.isArray(contract.extended_until)) {
                    data.extended_until = contract.extended_until;
                }
                const { status } = await provider(UPDATE, '/contracts', {
                    id: contract._id || contract.id,
                    data,
                });
                if (status >= 200 && status < 300) {
                    if (onSave) onSave();
                    onClose();
                }
            } else {
                const { status } = await provider(CREATE, '/contracts', { data });
                if (status >= 200 && status < 300) {
                    if (onSave) onSave();
                    onClose();
                }
            }
        } catch (error) {
            console.error('Error saving contract:', error);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="pc-emp-ov"
            onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}
        >
            <div className="pc-emp-modal pc-emp-modal--sm" role="dialog" aria-modal="true" aria-labelledby="pc-emp-contract-title">
                <div className="pc-emp-mh">
                    <div>
                        <h3 id="pc-emp-contract-title">{isEdit ? 'Редактирование контракта' : 'Добавление контракта'}</h3>
                        <div className="sub">{isEdit ? (contract.number || 'без номера') : 'Трудовой договор или подряд'}</div>
                    </div>
                    <button type="button" className="pc-emp-btn pc-emp-btn--ico pc-emp-btn--quiet" onClick={onClose} title="Закрыть">
                        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
                            <path d="M4 4l8 8M12 4l-8 8" />
                        </svg>
                    </button>
                </div>

                <div className="pc-emp-mb">
                    <div className="pc-emp-sect">
                        <div className="pc-emp-sect__t">Основные данные</div>
                        <div className="pc-emp-fg">
                            <Field label="Тип" required error={showError('type')}>
                                <select
                                    className={`pc-emp-inp${showError('type') ? ' is-invalid' : ''}`}
                                    value={form.type}
                                    onChange={setType}
                                    onBlur={() => mark('type')}
                                >
                                    <option value="">Не указано</option>
                                    {contractTypes.map((item) => (
                                        <option key={item.id} value={item.id}>{item.name}</option>
                                    ))}
                                </select>
                            </Field>
                            <Field label="Номер контракта" required error={showError('number')}>
                                <input
                                    className={`pc-emp-inp pc-emp-inp--mono${showError('number') ? ' is-invalid' : ''}`}
                                    value={form.number}
                                    onChange={set('number')}
                                    onBlur={() => mark('number')}
                                />
                            </Field>
                            <Field label="Дата начала" required error={showError('start')}>
                                <input
                                    className={`pc-emp-inp pc-emp-inp--mono${showError('start') ? ' is-invalid' : ''}`}
                                    type="date"
                                    value={form.start}
                                    onChange={set('start')}
                                    onBlur={() => mark('start')}
                                />
                            </Field>
                            <Field label="Дата окончания" required error={showError('end')}>
                                <input
                                    className={`pc-emp-inp pc-emp-inp--mono${showError('end') ? ' is-invalid' : ''}`}
                                    type="date"
                                    value={form.end}
                                    onChange={set('end')}
                                    onBlur={() => mark('end')}
                                />
                            </Field>
                            {!isContractor && isContract && (
                                <>
                                    <Field label="Ставка" required error={showError('parttime')}>
                                        <select
                                            className={`pc-emp-inp${showError('parttime') ? ' is-invalid' : ''}`}
                                            value={form.parttime}
                                            onChange={set('parttime')}
                                            onBlur={() => mark('parttime')}
                                        >
                                            {parttimeTypes.map((item) => (
                                                <option key={item.id} value={item.id}>{item.name}</option>
                                            ))}
                                        </select>
                                    </Field>
                                    {form.parttime && form.parttime !== 'Full' && (
                                        <label className="pc-emp-check">
                                            <input type="checkbox" checked={form.is_primary} onChange={setCheck('is_primary')} />
                                            Основное место работы
                                        </label>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    <div className="pc-emp-sect" style={{ marginBottom: 0 }}>
                        <div className="pc-emp-sect__t">Дополнительно</div>
                        <div className="pc-emp-fg">
                            {!isContractor && isContract && (
                                <>
                                    <label className="pc-emp-check">
                                        <input type="checkbox" checked={form.assignment} onChange={setCheck('assignment')} />
                                        Распределение
                                    </label>
                                    {form.assignment && (
                                        <Field label="Дата распределения" required error={showError('assignment_date')}>
                                            <input
                                                className={`pc-emp-inp pc-emp-inp--mono${showError('assignment_date') ? ' is-invalid' : ''}`}
                                                type="date"
                                                value={form.assignment_date}
                                                onChange={set('assignment_date')}
                                                onBlur={() => mark('assignment_date')}
                                            />
                                        </Field>
                                    )}
                                </>
                            )}
                            <Field label="Досрочное расторжение" span={2} error={showError('early_termination_date')}>
                                <input
                                    className={`pc-emp-inp pc-emp-inp--mono${showError('early_termination_date') ? ' is-invalid' : ''}`}
                                    type="date"
                                    value={form.early_termination_date}
                                    onChange={set('early_termination_date')}
                                    onBlur={() => mark('early_termination_date')}
                                />
                            </Field>
                            <Field label="Комментарий" span={2}>
                                <textarea
                                    className="pc-emp-inp pc-emp-area"
                                    rows={3}
                                    value={form.comment}
                                    onChange={set('comment')}
                                />
                            </Field>
                        </div>
                    </div>
                </div>

                <div className="pc-emp-mf">
                    {!canSave ? (
                        <span className="pc-emp-fhint">Заполните обязательные поля, отмеченные *</span>
                    ) : null}
                    <button type="button" className="pc-emp-btn pc-emp-btn--pri" disabled={saving || !canSave} onClick={handleSave}>
                        Сохранить
                    </button>
                    <button type="button" className="pc-emp-btn" onClick={onClose}>
                        Закрыть
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ContractModal;
